import React, { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Session } from "@models/session";
import { ActivityType } from "@models/activity-type";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { DateUtil } from "@utils/date.util";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { SENTIMENT_EMOJI } from "@models/sentiment";
import { DashboardClickableTile } from "@components/Home/DashboardClickableTile";

const SummaryText = styled.span`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  display: -webkit-box;
  overflow: hidden;
  font-size: 15px;
  line-height: 1.55;
`;

const ConversationItem: FC<{
  session: Session;
}> = ({ session }) => {
  const { t } = useTranslation();

  const title = useMemo(() => {
    return t(`sessions.types.${session.activityType}`);
  }, [session, t]);

  const sessionDate = useMemo(
    () => DateUtil.formatTime(session.createdDate),
    [session],
  );

  const isGallery = session.activityType === ActivityType.GallerySession;

  return (
    <DashboardClickableTile to={`transcripts/${session.id}`}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="8px"
      >
        <Box
          display="flex"
          alignItems="center"
          gap="6px"
          minWidth={0}
          marginBottom="6px"
        >
          <Typography
            color="textSecondary"
            fontSize="20px"
            fontWeight="500"
            lineHeight="30px"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {title}
          </Typography>
          {session.sentiment && (
            <Typography
              component="span"
              aria-label={t(`sentiment.values.${session.sentiment}`)}
              role="img"
              sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}
            >
              {SENTIMENT_EMOJI[session.sentiment]}
            </Typography>
          )}
        </Box>

        <Box sx={{ color: "primary.main", display: "flex" }}>
          {isGallery ? (
            <InsertPhotoOutlinedIcon fontSize="small" />
          ) : (
            <SmsOutlinedIcon fontSize="small" />
          )}
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        fontSize="14px"
        lineHeight="22px"
        fontWeight="400"
        color="#171A1FFF"
        gap="8px"
      >
        <SummaryText>{session.summary}</SummaryText>
        <span style={{ whiteSpace: "nowrap" }}>{sessionDate}</span>
      </Box>
    </DashboardClickableTile>
  );
};

export default ConversationItem;
